import { t } from 'i18next';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { blobToDataURL } from '../../../api';
import {
  DeliveryDTO,
  DeliveryService,
  MaterialClassificationItemDTO,
  PredictionsService,
} from '../../../api/generated';
import { extractDeliveryEntries } from '../../Delivery/deliveryConfig';
import { BoundingBox } from '../ImageCard';
import { EdgeEstimationEntry } from '../../Prediction/types';
import { shouldDisplayEdgeEstimation } from '../../Prediction/utils';
import { transformMaterialClassification } from '../../../api/queries/materialClassification';
import { BOUNDING_BOX_LIMIT, transformEdgeEstimation } from '../../../api/queries/edgeEstimation';

const PDF_TITLE_FONT_SIZE = 16;
const PDF_IMAGE_LABEL_FONT_SIZE = 12;
const PDF_TABLE_FONT_SIZE = 10;
const PDF_DISCLAIMER_FONT_SIZE = 8;
const PDF_DEFAULT_TITLE_Y = 22;
const PDF_DEFAULT_CONTENT_START_Y = 30;
const PDF_LEFT_MARGIN = 14;

const PDF_MAX_IMAGE_WIDTH = 180;
const PDF_MAX_IMAGE_HEIGHT = 120;
const PDF_IMAGE_PADDING_Y = 10;
const PDF_IMAGE_LABEL_OFFSET_Y = 8;
const PDF_BOUNDING_BOX_RADIUS = 0.5;
const PDF_EDGE_BOUNDING_BOX_LINE_WIDTH = 0.25;
const PDF_EDGE_LABEL_FONT_SIZE = 9;
const PDF_EDGE_HORIZONTAL_PADDING = pxToMm(16);
const PDF_EDGE_HORIZONTAL_SPACE_THRESHOLD = pxToMm(26);
const PDF_EDGE_HORIZONTAL_OFFSET_ABOVE = pxToMm(2);
const PDF_EDGE_HORIZONTAL_OFFSET_BELOW = pxToMm(6);
const PDF_EDGE_VERTICAL_PADDING = pxToMm(16);
const PDF_EDGE_VERTICAL_SPACE_THRESHOLD = pxToMm(52);
const PDF_EDGE_VERTICAL_OFFSET_RIGHT = pxToMm(6);
const PDF_EDGE_LABEL_FONT_PX = 12;
const PDF_EDGE_LABEL_BORDER_RADIUS_PX = 2;
const PDF_EDGE_LABEL_SCALE = 2;
const PDF_EDGE_HORIZONTAL_LABEL_PADDING_X_PX = 2;
const PDF_EDGE_HORIZONTAL_LABEL_PADDING_Y_PX = 2;
const PDF_EDGE_VERTICAL_LABEL_PADDING_X_PX = 4;
const PDF_EDGE_VERTICAL_LABEL_PADDING_Y_PX = 2;
const PDF_EDGE_LABEL_FONT = `${PDF_EDGE_LABEL_FONT_PX}px Roboto, sans-serif`;

export interface PdfGenerationOptions {
  includeImage?: boolean;
  includeBoundingBoxes?: boolean;
}

export async function generateDeliveriesPDF(
  deliveries: DeliveryDTO[],
  capturer_id: string,
  options?: PdfGenerationOptions
): Promise<jsPDF> {
  if (deliveries.length === 0) {
    throw new Error('No deliveries provided');
  }

  const doc = new jsPDF();
  let isFirstDelivery = true;
  const { includeImage = true, includeBoundingBoxes = true } = options || {};

  for (const delivery of deliveries) {
    if (!isFirstDelivery) {
      doc.addPage();
    }
    isFirstDelivery = false;

    const finalY = await addDeliveryTable(doc, delivery, capturer_id, PDF_DEFAULT_TITLE_Y, PDF_DEFAULT_CONTENT_START_Y);

    if (includeImage) {
      await addDeliveryImage(doc, delivery, finalY, includeBoundingBoxes);
    }
  }

  return doc;
}

async function addDeliveryImage(
  doc: jsPDF,
  delivery: DeliveryDTO,
  startY: number,
  includeBoundingBoxes: boolean
): Promise<number> {
  if (!delivery.pictureIds || delivery.pictureIds.length === 0) {
    return startY;
  }

  try {
    const imageResponse = await DeliveryService.deliveryControllerGetDeliveryImage({
      path: {
        jobsiteId: delivery.jobsiteId,
        deliveryId: delivery.id,
        imageId: delivery.pictureIds[0],
      },
    });

    if (imageResponse.data) {
      const deliveryImageDataUrl = await blobToDataURL(imageResponse.data as Blob);
      const img = await loadImage(deliveryImageDataUrl);

      const { width, height } = calculateImageDimensions(
        img.width,
        img.height,
        PDF_MAX_IMAGE_WIDTH,
        PDF_MAX_IMAGE_HEIGHT
      );

      const imageY = startY + PDF_IMAGE_PADDING_Y;
      const imageX = PDF_LEFT_MARGIN;
      const imageContentY = imageY + PDF_IMAGE_LABEL_OFFSET_Y;

      doc.setFontSize(PDF_IMAGE_LABEL_FONT_SIZE);
      doc.text(t('recording.loadingArea'), PDF_LEFT_MARGIN, imageY);
      doc.addImage(deliveryImageDataUrl, 'JPEG', imageX, imageContentY, width, height);

      if (includeBoundingBoxes) {
        await addBoundingBoxes(doc, delivery, imageX, imageContentY, width, height);
      }

      return imageContentY + height;
    }
  } catch (error) {
    console.warn(`Failed to fetch delivery image for delivery ${delivery.delivery_id}:`, error);
  }

  return startY;
}

async function addBoundingBoxes(
  doc: jsPDF,
  delivery: DeliveryDTO,
  x: number,
  y: number,
  width: number,
  height: number
): Promise<void> {
  const imageId = delivery.pictureIds[0];
  if (!imageId) return;

  try {
    const materialClassification = await PredictionsService.predictionControllerGetMaterialClassificationByImageId({
      path: { jobsiteId: delivery.jobsiteId, imageId },
    });
    const materialClass = transformMaterialClassification(
      materialClassification.data?.output as MaterialClassificationItemDTO[]
    );
    const displayEdgeEstimation = shouldDisplayEdgeEstimation(materialClass.rawPredictions);

    if (!displayEdgeEstimation) return;

    const response = await PredictionsService.predictionControllerGetEdgeSizesPredictionByImageId({
      path: { jobsiteId: delivery.jobsiteId, imageId },
    });

    if (response.data && response.data.output) {
      const edgeEstimation = transformEdgeEstimation(response.data.output as EdgeEstimationEntry[])[0];
      if (edgeEstimation.status === 'CRITICAL') {
        drawBoundingBoxes(doc, edgeEstimation.boundingBoxes, x, y, width, height);
        doc.setFontSize(PDF_DISCLAIMER_FONT_SIZE);
        doc.text(
          t('attributes.edge-sizes.boundingBoxesDisclaimer', {
            count: edgeEstimation.objectsAboveThreshold,
            maxCount: BOUNDING_BOX_LIMIT,
          }),
          PDF_LEFT_MARGIN,
          y + height + PDF_IMAGE_LABEL_OFFSET_Y
        );
      }
    }
  } catch (error) {
    console.warn(`Failed to fetch edge estimation for delivery ${delivery.delivery_id}:`, error);
  }
}

function drawBoundingBoxes(
  doc: jsPDF,
  boundingBoxes: BoundingBox[],
  x: number,
  y: number,
  width: number,
  height: number
): void {
  boundingBoxes.forEach((bb) => {
    const rectX = x + bb.rel_x1 * width;
    const rectY = y + bb.rel_y1 * height;
    const rectW = (bb.rel_x2 - bb.rel_x1) * width;
    const rectH = (bb.rel_y2 - bb.rel_y1) * height;

    if (bb.color === 'danger') {
      doc.setDrawColor(255, 0, 0); // Red
    } else {
      doc.setDrawColor(255, 255, 0); // Yellow
    }

    doc.setLineWidth(PDF_EDGE_BOUNDING_BOX_LINE_WIDTH);
    doc.roundedRect(rectX, rectY, rectW, rectH, PDF_BOUNDING_BOX_RADIUS, PDF_BOUNDING_BOX_RADIUS);
    drawBoundingBoxLabels(doc, bb, rectX, rectY, rectW, rectH, x, y, width, height);
  });
}

function drawBoundingBoxLabels(
  doc: jsPDF,
  boundingBox: BoundingBox,
  rectX: number,
  rectY: number,
  rectW: number,
  rectH: number,
  imageX: number,
  imageY: number,
  imageWidth: number,
  imageHeight: number
): void {
  doc.setFontSize(PDF_EDGE_LABEL_FONT_SIZE);
  doc.setTextColor(0, 0, 0);

  if (boundingBox.horizontalLabel) {
    drawHorizontalLabel(doc, boundingBox.horizontalLabel, rectX, rectY, rectW, imageX, imageY, imageWidth);
  }

  if (boundingBox.verticalLabel) {
    drawVerticalLabel(doc, boundingBox.verticalLabel, rectX, rectY, rectH, imageX, imageY, imageHeight);
  }
}

function drawHorizontalLabel(
  doc: jsPDF,
  label: string,
  rectX: number,
  rectY: number,
  rectW: number,
  imageX: number,
  imageY: number,
  imageWidth: number
): void {
  const labelImage = createEdgeLabelImage(label, 'horizontal');
  const placement = getHorizontalLabelPlacement(rectX + rectW / 2, rectY, imageX, imageY, imageWidth);
  const labelX = placement.left - labelImage.width / 2;
  const labelY = placement.placeAbove ? placement.top - labelImage.height : placement.top;

  doc.addImage(labelImage.dataUrl, 'PNG', labelX, labelY, labelImage.width, labelImage.height);
}

function drawVerticalLabel(
  doc: jsPDF,
  label: string,
  rectX: number,
  rectY: number,
  rectH: number,
  imageX: number,
  imageY: number,
  imageHeight: number
): void {
  const labelImage = createEdgeLabelImage(label, 'vertical');
  const placement = getVerticalLabelPlacement(rectX, rectY + rectH / 2, imageX, imageY, imageHeight);
  const labelX = placement.placeLeft ? placement.left - labelImage.width : placement.left;
  const labelY = placement.top - labelImage.height / 2;

  doc.addImage(labelImage.dataUrl, 'PNG', labelX, labelY, labelImage.width, labelImage.height);
}

function getHorizontalLabelPlacement(
  xCenter: number,
  y: number,
  imageLeft: number,
  imageTop: number,
  imageWidth: number
): { left: number; top: number; placeAbove: boolean } {
  const hasSpaceAbove = y - imageTop >= PDF_EDGE_HORIZONTAL_SPACE_THRESHOLD;

  return {
    left: clamp(xCenter, imageLeft + PDF_EDGE_HORIZONTAL_PADDING, imageLeft + imageWidth - PDF_EDGE_HORIZONTAL_PADDING),
    top: hasSpaceAbove ? y - PDF_EDGE_HORIZONTAL_OFFSET_ABOVE : y + PDF_EDGE_HORIZONTAL_OFFSET_BELOW,
    placeAbove: hasSpaceAbove,
  };
}

function getVerticalLabelPlacement(
  x: number,
  yCenter: number,
  imageLeft: number,
  imageTop: number,
  imageHeight: number
): { left: number; top: number; placeLeft: boolean } {
  const hasSpaceLeft = x - imageLeft >= PDF_EDGE_VERTICAL_SPACE_THRESHOLD;

  return {
    left: hasSpaceLeft ? x - 0.5 : x + PDF_EDGE_VERTICAL_OFFSET_RIGHT,
    top: clamp(yCenter, imageTop + PDF_EDGE_VERTICAL_PADDING, imageTop + imageHeight - PDF_EDGE_VERTICAL_PADDING),
    placeLeft: hasSpaceLeft,
  };
}

function createEdgeLabelImage(
  label: string,
  rotation: 'horizontal' | 'vertical'
): {
  dataUrl: string;
  width: number;
  height: number;
} {
  const horizontalPaddingPx =
    rotation === 'horizontal' ? PDF_EDGE_HORIZONTAL_LABEL_PADDING_X_PX : PDF_EDGE_VERTICAL_LABEL_PADDING_X_PX;
  const verticalPaddingPx =
    rotation === 'horizontal' ? PDF_EDGE_HORIZONTAL_LABEL_PADDING_Y_PX : PDF_EDGE_VERTICAL_LABEL_PADDING_Y_PX;
  const baseCanvas = document.createElement('canvas');
  const baseContext = baseCanvas.getContext('2d');

  if (!baseContext) {
    throw new Error('Failed to create PDF edge label canvas context');
  }

  const textMetrics = measureEdgeLabel(baseContext, label);
  const labelWidthPx = Math.ceil(textMetrics.width + horizontalPaddingPx * 2);
  const labelHeightPx = Math.ceil(textMetrics.height + verticalPaddingPx * 2);
  const canvasWidthPx = labelWidthPx;
  const canvasHeightPx = labelHeightPx;

  baseCanvas.width = canvasWidthPx * PDF_EDGE_LABEL_SCALE;
  baseCanvas.height = canvasHeightPx * PDF_EDGE_LABEL_SCALE;
  baseContext.scale(PDF_EDGE_LABEL_SCALE, PDF_EDGE_LABEL_SCALE);
  baseContext.font = PDF_EDGE_LABEL_FONT;
  baseContext.textAlign = 'center';
  baseContext.textBaseline = 'top';
  baseContext.fillStyle = 'rgba(255, 255, 255, 0.7)';
  drawRoundedRect(baseContext, 0, 0, labelWidthPx, labelHeightPx, PDF_EDGE_LABEL_BORDER_RADIUS_PX);
  baseContext.fill();
  baseContext.fillStyle = '#000000';
  baseContext.fillText(label, canvasWidthPx / 2, verticalPaddingPx);

  if (rotation === 'horizontal') {
    return {
      dataUrl: baseCanvas.toDataURL('image/png'),
      width: pxToMm(canvasWidthPx),
      height: pxToMm(canvasHeightPx),
    };
  }

  const rotatedCanvas = document.createElement('canvas');
  const rotatedContext = rotatedCanvas.getContext('2d');

  if (!rotatedContext) {
    throw new Error('Failed to create rotated PDF edge label canvas context');
  }

  rotatedCanvas.width = baseCanvas.height;
  rotatedCanvas.height = baseCanvas.width;
  rotatedContext.translate(0, rotatedCanvas.height);
  rotatedContext.rotate(-Math.PI / 2);
  rotatedContext.drawImage(baseCanvas, 0, 0);

  return {
    dataUrl: rotatedCanvas.toDataURL('image/png'),
    width: pxToMm(rotatedCanvas.width / PDF_EDGE_LABEL_SCALE),
    height: pxToMm(rotatedCanvas.height / PDF_EDGE_LABEL_SCALE),
  };
}

function measureEdgeLabel(context: CanvasRenderingContext2D, label: string): { width: number; height: number } {
  context.font = PDF_EDGE_LABEL_FONT;
  const metrics = context.measureText(label);
  const fallbackHeight = PDF_EDGE_LABEL_FONT_PX;

  return {
    width: metrics.width,
    height: Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent || fallbackHeight),
  };
}

function drawRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

async function addDeliveryTable(
  doc: jsPDF,
  delivery: DeliveryDTO,
  capturer_id: string,
  titleY: number,
  contentStartY: number
): Promise<number> {
  doc.setFontSize(PDF_TITLE_FONT_SIZE);
  const title = `${t('delivery.delivery_note')} - ${delivery.delivery_id || delivery.id}`;
  doc.text(title, PDF_LEFT_MARGIN, titleY);

  const deliveryEntries = extractDeliveryEntries(delivery, capturer_id);
  const tableData = deliveryEntries.map(({ label, value }) => [t(label), String(value)]);

  autoTable(doc, {
    body: tableData,
    startY: contentStartY,
    styles: { fontSize: PDF_TABLE_FONT_SIZE },
    pageBreak: 'avoid',
  });

  const finalY = (doc as any).lastAutoTable.finalY || contentStartY;
  return finalY;
}

function calculateImageDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  const aspectRatio = originalWidth / originalHeight;

  let width = maxWidth;
  let height = maxHeight;

  if (originalWidth > originalHeight) {
    height = width / aspectRatio;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
  } else {
    width = height * aspectRatio;
    if (width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatio;
    }
  }

  return { width, height };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function pxToMm(value: number): number {
  return (value * 25.4) / 96;
}

async function loadImage(imageDataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageDataUrl;
  });
}
